/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { environment } from '../../../../environments/environment';
import { NotificationApiService, UserApiService } from '@shared/common/api-services';
import { CurrentUserModel } from '@shared/common/models';
import { Howl } from 'howler';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { Store } from '@ngxs/store';
import {
    AddRealTimeNotification,
    IncrementUnreadCount,
} from '../../../layouts/components/toolbar/toolbar-notifications/state/notifications.state';
import { Notification } from '@shared/common/interfaces';
import { NotificationCoordinationService } from '../../../layouts/components/toolbar/toolbar-notifications/services/notification-coordination.service';
import { isRegisteredFor, readWebPushOwner, webPushOwnerValue } from './web-push-owner';

@Component({
    selector: 'vex-web-push',
    template: '',
    standalone: true,
    imports: [CommonModule, MatSnackBarModule],
})
export class WebPushComponent implements OnInit, OnDestroy {
    private swPush = inject(SwPush);
    private matSnackBar = inject(MatSnackBar);
    private router = inject(Router);
    private store = inject(Store);
    private subs = new SubSink();
    private storageKey = environment.storageKeys.webPushSubcription;
    private notificationApiService = inject(NotificationApiService);
    private userApiService = inject(UserApiService);
    private currentUser = inject(CurrentUserModel);
    private notificationCoordination = inject(NotificationCoordinationService);
    private publicKey = environment.webPush.publicKey;

    ngOnInit(): void {
        this.initializeWebPushNotifications();
        this.listenForNotifications();
    }

    private initializeWebPushNotifications(): void {
        // Ensure user data is loaded before setting up push notifications
        this.subs.sink = this.userApiService.getMe().subscribe({
            next: (user) => {
                console.log('User data loaded for WebPush:', { userId: user._id });
                this.subscribeToPushNotifications();
            },
            error: (error) => {
                console.error('Error loading user data for WebPush:', error);
            },
        });
    }

    private subscribeToPushNotifications() {
        console.log('Attempting to subscribe to push notifications...');
        console.log('SwPush enabled:', this.swPush.isEnabled);
        console.log('User ID exists:', !!this.currentUser?._id);

        if (!this.swPush.isEnabled || !this.currentUser?._id) {
            console.warn('Push notifications not available:', {
                swPushEnabled: this.swPush.isEnabled,
                userIdExists: !!this.currentUser?._id,
            });
            return;
        }

        this.swPush.subscription.subscribe(async (subscription) => {
            console.log('Subscription status changed:', subscription);
            if (!subscription) {
                console.log('No subscription found, requesting new one...');
                this.requestPushNotificationSubscription();
                return;
            }

            console.log('Subscription exists:', subscription.endpoint);
            const owner = readWebPushOwner(localStorage.getItem(this.storageKey));
            if (isRegisteredFor(owner, this.currentUser._id, subscription.endpoint)) return;

            // This browser's subscription was registered for another admin (or
            // before the owner was recorded): register it for the current one.
            this.registerSubscription(subscription);
        });

        this.swPush.notificationClicks.subscribe((event) => {
            const { url, route } = event.notification.data || {};
            if (url) window.open(url, '_blank');
            if (route) this.router.navigate([route]);
        });
    }

    private async requestPushNotificationSubscription() {
        try {
            const subscription = await this.swPush.requestSubscription({
                serverPublicKey: this.publicKey,
            });

            if (!subscription) return;
            this.registerSubscription(subscription);
        } catch (error) {
            console.error('Could not subscribe to notifications', error);
        }
    }

    // The server stores subscriptions by token and reassigns an existing one to
    // whoever registers it, so this also takes it over from a previous admin.
    private registerSubscription(subscription: PushSubscription) {
        const userId = this.currentUser._id;
        this.notificationApiService.register(userId, JSON.stringify(subscription)).subscribe({
            next: () => {
                localStorage.setItem(
                    this.storageKey,
                    webPushOwnerValue(userId, subscription.endpoint),
                );
            },
            error: (error) => {
                // Owner stays unrecorded, so the next page load tries again.
                console.error('Could not register push subscription', error);
            },
        });
    }

    private listenForNotifications() {
        this.subs.sink = this.swPush.messages.subscribe(
            (message: any) => {
                console.log('SwPush message received:', message);

                // Extract notificationId from the message
                const notificationId = message?.notification?.data?.notificationId;

                if (notificationId && notificationId.trim() !== '') {
                    // Try to fetch and add the complete notification data
                    this.notificationApiService.getNotificationById(notificationId).subscribe({
                        next: (notificationData: Notification) => {
                            // Add the full notification to the store (this will properly calculate unread count)
                            this.store.dispatch(new AddRealTimeNotification(notificationData));
                        },
                        error: (error) => {
                            console.error('Error fetching notification data:', error);
                            // Fallback: just increment the count if we can't fetch the notification
                            this.store.dispatch(new IncrementUnreadCount());
                        },
                    });
                } else {
                    console.warn('No valid notificationId found in push message');
                }

                if (!message?.notification?.data?.silent) {
                    console.log('Showing snackbar and playing sound');
                    this.showSnackbar(message.notification);
                    this.playSound();
                }
            },
            (error) => {
                console.error('❌ Error in SwPush messages subscription:', error);
            },
            () => {
                console.log('SwPush messages subscription completed');
            },
        );

        this.subs.sink = this.swPush.notificationClicks.subscribe((event) => {
            console.log('Notification Clicked: ', event);
            const { url, route } = event.notification.data || {};
            if (url) window.open(url, '_blank'); // Opens a new tab
            if (route) this.router.navigate([route]); // In-app navigation
        });
    }

    private showSnackbar(notification: any) {
        const { body, action, data = {} } = notification || {};
        const snackBarRef = this.matSnackBar.open(body, action || 'Close', {
            panelClass: data.alert || '',
            duration: 5000,
        });

        if (!data.route && !data.url) return;
        this.subs.sink = snackBarRef.onAction().subscribe(() => {
            if (data.url) window.open(data.url, '_blank');
            if (data.route) this.router.navigate([data.route]);
        });
    }

    private playSound() {
        new Howl({
            src: ['/assets/sounds/notify.mp3'],
        }).play();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
